/**
 * 채널 이름을 화면에 적는 **한 곳** — 정본은 `constants/platform-catalog` 다.
 *
 * ```
 *   Threads · X · Instagram · Youtube · TikTok
 *   DC · TheQoo · 뽐뿌 · N.Kin · N.Cafe · N.Blog · Reddit · LIPS · @cosme
 * ```
 *
 * ⛔ **화면마다 제 이름표를 만들지 마라.** 한때 같은 채널이 자리마다 달리 불렸다 —
 *   소재 찾기는 「네이버 블로그」, 허브는 「인스타그램」, 사이드바는 「N.Blog」·「Instagram」.
 *   같은 값을 다르게 부르면 두 화면이 같은 것을 말하는지 사용자가 못 가린다.
 * ⚠️ 광고 원장의 `meta` · 노출 위치 `facebook` 은 **카탈로그에 없다** — 소셜 채널이 아니라 광고 매체라서다. 여기서 이름을 준다.
 */

import { getPlatformInfo } from 'src/utils/platform/platform-info';

/** 카탈로그 밖의 값 — 광고 원장은 소셜 채널이 아니다 */
const EXTRA_LABELS: Record<string, string> = {
  meta: 'Meta 광고',
  facebook: 'Facebook',
};

/**
 * @param channel 색인의 `source` 값(`instagram` · `naverblog` · `meta` …)
 * @returns 화면에 적는 이름. 모르는 값이면 받은 값을 그대로 돌려준다(빈칸보다 낫다)
 */
export function channelLabel(channel: string | null | undefined): string {
  const key = String(channel ?? '').trim();
  if (!key) return '';
  if (EXTRA_LABELS[key]) return EXTRA_LABELS[key];
  return getPlatformInfo(key).label || key;
}

/**
 * 나라를 못 가린 값들 — **색인에 실제로 있는 것만** 적는다.
 * 소셜은 `OTHER`, 광고는 `unknown`, 화면이 묶은 나머지 칸은 `others` 다.
 * ⚠️ **빈 값은 여기 없다** — 「아예 없음」(빈칸으로 두어야 하는 자리)과 「모았는데 못 가림」(「미상」)은 다르다.
 * ⛔ **「전체」 뜻의 값을 여기 넣지 마라** — 조건 칸이 「전체」로 쓰는 값이 「미상」으로 적히면 거짓이 된다.
 */
const UNKNOWN_COUNTRY = new Set(['UNKNOWN', 'OTHER', 'OTHERS']);

/** 나라를 못 가렸을 때 적는 말 — ⛔ 「UNKNOWN」·「🌏 Others」를 그대로 내보내지 마라(값이 아니라 빈칸이다) */
export const UNKNOWN_COUNTRY_LABEL = '미상';

/**
 * 국가 코드를 화면에 적는 모양 — **대문자 코드 그대로**다(`KR` · `US` · `JP`), 못 가린 것은 「미상」.
 *
 * 🔑 조건 칸(`filter-country-section`)이 국기 + 코드로 적으므로 나머지 화면도 코드로 맞춘다 —
 *   한 화면은 「한국」, 다른 화면은 「KR」이면 같은 조건인지 안 보인다. 국기까지 붙이려면 `CountryTag`.
 * ⚠️ 이름(「한국」)으로 적지 마라 — 248개국을 다 한글 이름으로 갖고 있지 않아 어떤 나라는 코드로,
 *   어떤 나라는 이름으로 나오는 뒤죽박죽이 된다.
 */
export function countryLabel(code: string | null | undefined): string {
  const key = String(code ?? '').trim().toUpperCase();
  if (!key) return '';
  return UNKNOWN_COUNTRY.has(key) ? UNKNOWN_COUNTRY_LABEL : key;
}

/**
 * 국기를 그릴 수 있는 코드만 돌려준다 — 아니면 빈 문자열.
 * ⚠️ 두 글자 ISO 코드가 아니면 국기 그림이 **없다**(깨진 그림이 뜬다) — 그래서 먼저 거른다.
 */
export function countryFlagCode(code: string | null | undefined): string {
  const key = String(code ?? '').trim().toUpperCase();
  if (!key || UNKNOWN_COUNTRY.has(key)) return '';
  return /^[A-Z]{2}$/.test(key) ? key : '';
}
